import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Plus, Pencil } from "lucide-react";
import {
  atualizarTabelaDistribuidor,
  criarTabelaDistribuidor,
  listarModulosCatalogo,
  listarTabelasDistribuidor,
  obterTabelaDistribuidor,
} from "../services/api";

const UNIDADES = [
  ["licenca", "Por licença"],
  ["terminal", "Por terminal"],
  ["empresa", "Por empresa"],
  ["dispositivo", "Por dispositivo"],
  ["estabelecimento", "Por estabelecimento"],
  ["mes", "Por mês"],
];

function unidadePadrao(codigo) {
  if (codigo === "ZENTAG_BASE" || codigo === "ZENMOBILE_BASE" || codigo === "ZENPASS_BASE") return "mes";
  if (codigo === "ADD_EMPRESA") return "empresa";
  if (["ADD_ZENPAY", "ADD_PDV_POS", "ADD_PDV_ADICIONAL", "ADD_AUTOATENDIMENTO", "ADD_PREVENDA"].includes(codigo)) return "terminal";
  if (codigo === "ADD_WHATSAPP") return "estabelecimento";
  if (codigo === "ADD_INVENTARIO") return "dispositivo";
  return "licenca";
}

const TabelasPrecoDistribuidor = ({ token, criar = false }) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const editando = Boolean(id);
  const form = criar || editando;
  const [tabelas, setTabelas] = useState([]);
  const [modulos, setModulos] = useState([]);
  const [nome, setNome] = useState("");
  const [descricao, setDescricao] = useState("");
  const [ativo, setAtivo] = useState(true);
  const [valores, setValores] = useState({});
  const [unidades, setUnidades] = useState({});
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(form);

  const carregarLista = async () => {
    try {
      const res = await listarTabelasDistribuidor(token);
      setTabelas(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      console.error(e);
      setTabelas([]);
    }
  };

  useEffect(() => {
    if (!form) {
      carregarLista();
      return;
    }
    setLoading(true);
    const mods = listarModulosCatalogo(token).then((res) => (Array.isArray(res.data) ? res.data : []));
    const atual = editando
      ? obterTabelaDistribuidor(id, token).then((res) => res.data)
      : Promise.resolve(null);
    Promise.all([mods, atual])
      .then(([lista, tabela]) => {
        setModulos(lista);
        const un = {};
        lista.forEach((m) => {
          un[m.id] = unidadePadrao(m.codigo);
        });
        if (tabela) {
          setNome(tabela.nome || "");
          setDescricao(tabela.descricao || "");
          setAtivo(tabela.ativo !== false);
          const vals = {};
          (tabela.itens || []).forEach((item) => {
            vals[item.moduloId] = String(item.valorUnitario ?? "").replace(".", ",");
            if (item.unidadeCobranca) un[item.moduloId] = item.unidadeCobranca;
          });
          setValores(vals);
        }
        setUnidades(un);
      })
      .catch(() => setModulos([]))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, criar, id]);

  const salvar = async (e) => {
    e.preventDefault();
    const itens = modulos
      .map((m) => ({
        moduloId: m.id,
        valorUnitario: Number(String(valores[m.id] || "0").replace(",", ".")),
        unidadeCobranca: unidades[m.id] || unidadePadrao(m.codigo),
      }))
      .filter((i) => i.valorUnitario > 0);
    setSaving(true);
    try {
      const payload = { nome: nome.trim(), descricao: descricao.trim(), ativo, itens };
      if (editando) await atualizarTabelaDistribuidor(id, payload, token);
      else await criarTabelaDistribuidor(payload, token);
      navigate("/tabelas-preco");
    } catch (err) {
      const data = err?.response?.data;
      alert(typeof data === "string" ? data : data?.message || "Erro ao salvar a tabela.");
    } finally {
      setSaving(false);
    }
  };

  if (form) {
    if (loading) return <div className="p-6 text-center">Carregando tabela...</div>;
    return (
      <div className="max-w-4xl mx-auto">
        <form onSubmit={salvar} className="zen-card rounded-2xl p-6 shadow-sm space-y-4 dark:backdrop-blur">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
            {editando ? "Editar tabela de preço" : "Nova tabela de preço"}
          </h2>
          <input required value={nome} onChange={(e) => setNome(e.target.value)} placeholder="Nome" className="w-full px-3 py-2 rounded-xl border dark:bg-slate-950/40 dark:border-white/10" />
          <input value={descricao} onChange={(e) => setDescricao(e.target.value)} placeholder="Descrição" className="w-full px-3 py-2 rounded-xl border dark:bg-slate-950/40 dark:border-white/10" />
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={ativo} onChange={(e) => setAtivo(e.target.checked)} />
            Tabela ativa
          </label>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr>
                  <th className="text-left p-2">Módulo</th>
                  <th className="text-left p-2">Produto</th>
                  <th className="text-left p-2">Cobrança</th>
                  <th className="text-right p-2">Valor</th>
                </tr>
              </thead>
              <tbody>
                {modulos.map((m) => (
                  <tr key={m.id} className="border-t dark:border-white/10">
                    <td className="p-2">{m.nome}</td>
                    <td className="p-2">{m.produto}</td>
                    <td className="p-2">
                      <select
                        value={unidades[m.id] || "licenca"}
                        onChange={(e) => setUnidades((u) => ({ ...u, [m.id]: e.target.value }))}
                        className="px-2 py-1 rounded-lg border dark:bg-slate-950/40 dark:border-white/10"
                      >
                        {UNIDADES.map(([value, label]) => (
                          <option key={value} value={value}>{label}</option>
                        ))}
                      </select>
                    </td>
                    <td className="p-2 text-right">
                      <input
                        value={valores[m.id] || ""}
                        onChange={(e) => setValores((v) => ({ ...v, [m.id]: e.target.value }))}
                        className="w-28 px-2 py-1 rounded-lg border text-right dark:bg-slate-950/40 dark:border-white/10"
                        placeholder="0,00"
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => navigate("/tabelas-preco")} className="px-4 py-2 rounded-xl border text-sm dark:border-white/10">
              Voltar
            </button>
            <button type="submit" disabled={saving} className="px-4 py-2 rounded-xl bg-blue-600 text-white text-sm">
              {saving ? "Salvando..." : "Salvar tabela"}
            </button>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="zen-card rounded-2xl p-6 shadow-sm dark:backdrop-blur">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Tabelas de preço</h2>
            <p className="text-sm text-gray-500 dark:text-slate-300/80">Tabelas deste distribuidor, usadas nas revendas.</p>
          </div>
          <button type="button" onClick={() => navigate("/tabelas-preco/novo")} className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-blue-600 text-white text-sm">
            <Plus size={16} /> Nova tabela
          </button>
        </div>
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 dark:bg-white/5">
            <tr>
              <th className="text-left p-3">Nome</th>
              <th className="text-left p-3">Itens</th>
              <th className="text-left p-3">Status</th>
              <th className="p-3"></th>
            </tr>
          </thead>
          <tbody>
            {tabelas.map((t) => (
              <tr key={t.id} className="border-t dark:border-white/10">
                <td className="p-3">{t.nome}</td>
                <td className="p-3">{t.qtdItens ?? 0}</td>
                <td className="p-3">{t.ativo === false ? "Inativa" : "Ativa"}</td>
                <td className="p-3 text-right">
                  <button
                    type="button"
                    onClick={() => navigate(`/tabelas-preco/editar/${t.id}`)}
                    className="inline-flex items-center gap-1 text-blue-600 dark:text-blue-300"
                  >
                    <Pencil size={14} /> Editar
                  </button>
                </td>
              </tr>
            ))}
            {!tabelas.length && (
              <tr><td colSpan={4} className="p-6 text-center text-gray-500">Nenhuma tabela cadastrada.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default TabelasPrecoDistribuidor;
